import React, { useState, useEffect } from "react";
import "../AddAdvertisement.css";
import KeywordSelectionModal from "../components/KeywordSelectionModal";
import { getNaverShoppingRankAPI } from "../../../../../api";
import { isSpecialUser } from "../../../../../utils/environment";

const ShoppingVisitPageWithOrigin = ({
  formData,
  onInputChange,
  user,
  onNext,
  endSubmitTime,
  step = 1,
  keywordRanks = [],
  addKeywordRank,
  removeKeywordRank,
  updateKeywordRank,
  onKeywordRanksChange,
  zoomSettings,
}) => {
  const [currentStep, setCurrentStep] = useState(step);
  const [urlError, setUrlError] = useState("");
  const [originIdError, setOriginIdError] = useState("");
  const [isKeywordModalOpen, setIsKeywordModalOpen] = useState(false);
  const [keywordLoadingIndex, setKeywordLoadingIndex] = useState(null); // 키워드 순위 조회 중인 인덱스

  // step prop이 변경될 때 currentStep 업데이트
  useEffect(() => {
    setCurrentStep(step);
  }, [step]);

  // currentStep이 2가 되면 자동으로 키워드 모달 열기
  useEffect(() => {
    if (currentStep === 2) {
      setIsKeywordModalOpen(true);
    }
  }, [currentStep]);

  // adStartDate와 adEndDate가 변경될 때 광고 기간 자동 계산
  useEffect(() => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        // console.log("📅 useEffect로 광고 기간 계산:", {
        //   startDate: formData.adStartDate,
        //   endDate: formData.adEndDate,
        //   timeDiff: timeDiff,
        //   daysDiff: daysDiff,
        //   explanation: `${formData.adStartDate}부터 ${formData.adEndDate}까지 ${daysDiff}일 (시작일과 종료일 모두 포함)`,
        // });

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  }, [formData.adStartDate, formData.adEndDate]);

  // 내일 날짜 계산 (한국 시간 기준)
  const getTomorrowDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    const tomorrow = new Date(koreaTime);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // 오늘 날짜 계산 (한국 시간 기준)
  const getTodayDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    return koreaTime.toISOString().split("T")[0];
  };

  // 최소 날짜 (내일)
  const minDate = getTomorrowDate();
  const todayDate = getTodayDate();

  // 특정 유저는 오늘부터, 일반 유저는 내일부터
  const userIsSpecial = isSpecialUser(user?.id);
  const effectiveMinDate = userIsSpecial ? todayDate : minDate;

  // 광고 종료일의 최소 날짜 (광고 시작일이 있으면 그 날짜, 없으면 내일)
  const getEndDateMin = () => {
    if (formData.adStartDate) {
      return formData.adStartDate;
    }
    return effectiveMinDate;
  };

  // 날짜 검증 함수
  const validateDate = (dateString) => {
    if (!dateString) return "날짜를 선택해주세요.";

    const selectedDate = new Date(dateString);
    const tomorrow = new Date(getTomorrowDate());

    // 유효한 날짜인지 확인
    if (isNaN(selectedDate.getTime())) {
      return "올바른 날짜 형식이 아닙니다.";
    }

    // 특정 유저가 아닌 경우에만 내일 이후인지 확인
    if (!userIsSpecial && selectedDate < tomorrow) {
      return "광고 시작일은 내일 이후여야 합니다.";
    }

    return "";
  };

  // 광고 시작일 입력 처리
  const handleAdStartDateChange = (e) => {
    const value = e.target.value;
    onInputChange("adStartDate", value);

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adStartDate", "");
        alert(error);
      }
    }

    // 광고 기간 자동 계산
    calculateAdDuration();
  };

  // 광고 종료일 입력 처리
  const handleAdEndDateChange = (e) => {
    const value = e.target.value;
    onInputChange("adEndDate", value);

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adEndDate", "");
        alert(error);
      }
    }

    // 광고 기간 자동 계산
    calculateAdDuration();
  };

  // 광고 기간 자동 계산 함수
  const calculateAdDuration = () => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        // console.log("📅 광고 기간 계산:", {
        //   startDate: formData.adStartDate,
        //   endDate: formData.adEndDate,
        //   timeDiff: timeDiff,
        //   daysDiff: daysDiff,
        //   explanation: `${formData.adStartDate}부터 ${formData.adEndDate}까지 ${daysDiff}일 (시작일과 종료일 모두 포함)`,
        // });

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  };

  // 결제 금액 자동 계산 함수
  const calculatePrice = () => {
    const dailyQuantity = parseInt(formData.dailyQuantity) || 0;
    const adDuration = parseInt(formData.adDuration) || 0;
    const userPriceByOne = parseInt(formData.userPriceByOne) || 0;

    const totalQuantity = dailyQuantity * adDuration;
    const totalPrice = totalQuantity * userPriceByOne;

    onInputChange("totalQuantity", totalQuantity.toString());
    onInputChange("price", totalPrice.toString());
  };

  // 일일 수량, 광고 기간, 단가가 변경될 때마다 결제 금액 계산
  useEffect(() => {
    calculatePrice();
  }, [formData.dailyQuantity, formData.adDuration, formData.userPriceByOne]);

  // URL 포맷팅 함수
  const formatUrl = (url) => {
    if (!url) return "";

    // URL에서 쿼리 파라미터 제거
    let formattedUrl = url.split("?")[0];

    // 마지막 슬래시 제거
    formattedUrl = formattedUrl.replace(/\/$/, "");

    return formattedUrl;
  };

  // URL 검증 함수
  const validateUrl = (url) => {
    if (!url) return "";

    // 일반적인 URL 패턴 검증
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/;

    if (!urlPattern.test(url)) {
      return "올바른 URL 형식이 아닙니다.";
    }

    return "";
  };

  // URL 입력 처리
  const handleUrlChange = (e) => {
    const value = e.target.value;
    onInputChange("productUrl", value);
    setUrlError(""); // 입력 중에는 에러 메시지 제거
  };

  // URL 포커스 아웃 처리
  const handleUrlBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const formattedUrl = formatUrl(value);
      const error = validateUrl(formattedUrl);

      if (error) {
        setUrlError(error);
      } else {
        onInputChange("productUrl", formattedUrl);
        setUrlError("");
      }
    }
  };

  // 카테고리 URL 입력 처리
  const handleOriginIdChange = (e) => {
    const value = e.target.value;
    onInputChange("originId", value);
    setOriginIdError(""); // 입력 중에는 에러 메시지 제거
  };

  // 카테고리 URL 포커스 아웃 처리
  const handleOriginIdBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const formattedUrl = formatUrl(value);
      const error = validateUrl(formattedUrl);

      if (error) {
        setOriginIdError(error);
      } else {
        onInputChange("originId", formattedUrl);
        setOriginIdError("");
      }
    }
  };

  const handleSubmit = () => {
    if (!user?.id) {
      onNext();
      return;
    }
    setCurrentStep(2);
  };

  const handleBackFromConfirmation = () => {
    setCurrentStep(1);
  };

  const handleConfirmOrder = () => {
    setCurrentStep(3);
  };

  // 키워드 순위 조회 API 호출 함수
  const fetchKeywordRank = async (keyword, index) => {
    if (!keyword || !keyword.trim() || !formData.productId) {
      return;
    }

    // 로딩 상태 시작
    setKeywordLoadingIndex(index);

    try {
      const rankData = {
        main: {
          productId: formData.productId,
          name: keyword.trim(),
          pageSize: 1,
        },
        sub: [],
      };

      const response = await getNaverShoppingRankAPI(rankData);

      if (response.data?.main) {
        const rank = response.data.main.rank || 999;

        // 선택된 키워드들 중에서 해당 인덱스의 키워드를 업데이트
        const selectedKeywords = keywordRanks.filter((item) => item.selected);
        const actualIndex = keywordRanks.findIndex(
          (item) => item.selected && selectedKeywords[index] === item
        );

        if (actualIndex !== -1) {
          updateKeywordRank(actualIndex, "rank", rank);
        }
      }
    } catch (error) {
      console.error("🟡 키워드 순위 조회 실패:", error);
    } finally {
      // 로딩 상태 종료
      console.log("🟡 로딩 상태 종료");
      setKeywordLoadingIndex(null);
    }
  };

  // 키워드 입력 필드 포커스 아웃 처리
  const handleKeywordBlur = (keyword, index) => {
    const trimmedKeyword = keyword.trim();

    // 빈 문자열이 아니고 순위가 0인 경우에만 API 호출
    if (trimmedKeyword && trimmedKeyword !== "") {
      fetchKeywordRank(trimmedKeyword, index);
    }
  };

  // 키워드 입력 필드 키 다운 처리 (엔터 키)
  const handleKeywordKeyDown = (e, keyword, index, actualIndex) => {
    if (e.key === "Enter") {
      e.preventDefault(); // 기본 엔터 동작 방지
      const trimmedKeyword = keyword.trim();

      // 먼저 trim된 값으로 입력 필드 업데이트
      if (keyword !== trimmedKeyword) {
        updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
      }

      // 중복 검사 (대소문자 구분 없이, trim된 값으로 비교)
      if (trimmedKeyword !== "") {
        // keywordRanks 배열에서 직접 중복 키워드 찾기
        const duplicateActualIndex = keywordRanks.findIndex(
          (item, idx) =>
            idx !== actualIndex &&
            item.keyword.trim() !== "" &&
            item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
        );

        if (duplicateActualIndex !== -1) {
          const duplicateKeyword = keywordRanks[duplicateActualIndex];

          // 중복 키워드가 이미 selected: true인 경우 팝업 표시
          if (duplicateKeyword.selected) {
            alert("이미 등록된 키워드입니다.");
            // 중복이면 현재 입력 필드 제거
            removeKeywordRank(actualIndex);
            return;
          }

          // 중복 키워드가 selected: false인 경우 true로 변경하고 맨 뒤로 이동
          const updatedDuplicateKeyword = {
            ...duplicateKeyword,
            selected: true,
          };

          // 키워드를 배열에서 제거하고 맨 뒤에 추가
          const newKeywordRanks = [...keywordRanks];
          const isLastIndex = duplicateActualIndex === keywordRanks.length - 1;

          if (!isLastIndex) {
            const indicesToRemove = [duplicateActualIndex, actualIndex].sort(
              (a, b) => b - a
            );
            indicesToRemove.forEach((idx) => {
              newKeywordRanks.splice(idx, 1);
            });
            newKeywordRanks.push(updatedDuplicateKeyword); // 맨 뒤에 추가
          } else {
            newKeywordRanks[duplicateActualIndex] = updatedDuplicateKeyword;
            newKeywordRanks.splice(actualIndex, 1);
          }

          if (onKeywordRanksChange) {
            onKeywordRanksChange(newKeywordRanks);
          }
          return;
        }
      }

      // 빈 문자열이 아니고 순위가 0인 경우에만 API 호출
      if (trimmedKeyword && trimmedKeyword !== "") {
        fetchKeywordRank(trimmedKeyword, index);
      }

      // 포커스 해제
      e.target.blur();
    }
  };

  // 키워드 입력 변경 핸들러 (입력 중에는 원본 유지, blur/Enter 시 trim + 중복 검사)
  const handleKeywordChange = (e, actualIndex) => {
    const inputValue = e.target.value;
    updateKeywordRank(actualIndex, "keyword", inputValue);
  };

  // 키워드 blur 핸들러 (trim + 중복 검사)
  const handleKeywordBlurWithValidation = (keyword, index, actualIndex) => {
    const trimmedKeyword = keyword.trim();

    if (keyword !== trimmedKeyword) {
      updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
    }

    if (trimmedKeyword !== "") {
      const duplicateActualIndex = keywordRanks.findIndex(
        (item, idx) =>
          idx !== actualIndex &&
          item.keyword.trim() !== "" &&
          item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
      );

      if (duplicateActualIndex !== -1) {
        const duplicateKeyword = keywordRanks[duplicateActualIndex];

        if (duplicateKeyword.selected) {
          alert("이미 등록된 키워드입니다.");
          removeKeywordRank(actualIndex);
          return;
        }

        // 중복 키워드가 selected: false인 경우 true로 변경하고 맨 뒤로 이동
        const updatedDuplicateKeyword = { ...duplicateKeyword, selected: true };

        // 키워드를 배열에서 제거하고 맨 뒤에 추가
        const newKeywordRanks = [...keywordRanks];

        // 중복 키워드 제거 (인덱스가 큰 것부터 제거해야 인덱스가 꼬이지 않음)
        const indicesToRemove = [duplicateActualIndex, actualIndex].sort(
          (a, b) => b - a
        );
        indicesToRemove.forEach((idx) => {
          newKeywordRanks.splice(idx, 1);
        });

        newKeywordRanks.push(updatedDuplicateKeyword); // 맨 뒤에 추가

        // 부모 컴포넌트에 업데이트된 배열 전달 (한 번에 모든 변경사항 반영)
        if (onKeywordRanksChange) {
          onKeywordRanksChange(newKeywordRanks);
        }
        return;
      }
    }

    handleKeywordBlur(trimmedKeyword, index);
  };

  // 키워드 모달 핸들러들
  const handleOpenKeywordModal = () => {
    setIsKeywordModalOpen(true);
  };

  const handleCloseKeywordModal = () => {
    setIsKeywordModalOpen(false);
  };

  const handleKeywordRanksChange = (newKeywordRanks) => {
    // 부모 컴포넌트의 keywordRanks를 업데이트
    if (onKeywordRanksChange) {
      onKeywordRanksChange(newKeywordRanks);
    }
  };

  // 주문 확인 화면
  if (currentStep === 2) {
    return (
      <div className="shopping-visit-confirmation">
        {/* 1. 상품 정보 입력 섹션 */}
        <div className="product-info-section">
          <div className="product-image-container">
            <div className="product-image">
              {formData.imageUrl ? (
                <img
                  src={formData.imageUrl}
                  alt="상품 이미지"
                  className="product-image-display"
                />
              ) : (
                <div className="image-placeholder">
                  <span>상품 이미지</span>
                </div>
              )}
            </div>
          </div>

          <div className="product-info-grid">
            {/* 첫 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>카테고리 상품 URL</label>
                <input type="text" value={formData.originId || ""} readOnly />
              </div>
              <div className="form-field">
                <label>카테고리 상품명</label>
                <input
                  type="text"
                  value={formData.missionName || ""}
                  readOnly
                />
              </div>
            </div>

            {/* 두 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>상품 URL 주소</label>
                <input type="text" value={formData.productUrl || ""} readOnly />
              </div>
              <div className="form-field">
                <label>상품명</label>
                <input
                  type="text"
                  value={(() => {
                    try {
                      if (formData.memo) {
                        const memoData = JSON.parse(formData.memo);
                        return (
                          memoData.originProductName ||
                          formData.missionName ||
                          ""
                        );
                      }
                      return formData.missionName || "";
                    } catch (error) {
                      return formData.missionName || "";
                    }
                  })()}
                  readOnly
                />
              </div>
            </div>

            {/* 세 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>스토어명</label>
                <input type="text" value={formData.storeName || ""} readOnly />
              </div>
              <div className="form-field">
                <label>메인키워드(순위체크용)</label>
                <input
                  type="text"
                  value={formData.mainKeyword?.name || ""}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>상품번호</label>
                <input type="text" value={formData.productId || ""} readOnly />
              </div>
              <div className="form-field">
                <label>상품 이미지 주소</label>
                <input
                  type="text"
                  value={formData.imageUrl || ""}
                  placeholder="https://shopping..."
                  readOnly
                />
              </div>
            </div>

            {/* 네 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>일일 수량</label>
                <input
                  type="text"
                  value={`${formData.dailyQuantity || 0}회`}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>광고 기간</label>
                <input
                  type="text"
                  value={`${formData.adDuration || 0}일`}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>광고 시작일</label>
                <input
                  type="text"
                  value={formData.adStartDate || ""}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* 2. 키워드 자동 완성 섹션 */}
        <div className="keyword-section">
          <button
            className="keyword-autocomplete-btn"
            onClick={handleOpenKeywordModal}
          >
            유입키워드 자동완성
          </button>

          <div className="keyword-boxes">
            {/* 선택된 키워드들만 표시 */}
            {keywordRanks
              .filter((item) => item.selected)
              .map((item, index) => {
                // 선택된 키워드들 중에서 실제 인덱스 찾기
                const selectedKeywords = keywordRanks.filter(
                  (item) => item.selected
                );
                const actualIndex = keywordRanks.findIndex(
                  (keywordItem) =>
                    keywordItem.selected &&
                    selectedKeywords[index] === keywordItem
                );

                return (
                  <div
                    key={actualIndex}
                    className={
                      index === 0
                        ? item.rank > (zoomSettings?.allowRanking || 30)
                          ? "keyword-box error"
                          : item.rank === 0
                          ? "keyword-box empty"
                          : "keyword-box required"
                        : item.rank > (zoomSettings?.allowRanking || 30)
                        ? "keyword-box error"
                        : item.rank === 0
                        ? "keyword-box empty"
                        : "keyword-box optional"
                    }
                  >
                    <div className="keyword-content">
                      {index === 0 ? (
                        // 첫 번째 항목 (required) - X 버튼 있음
                        <>
                          <div className="keyword-header">
                            <div
                              className={`keyword-rank ${
                                item.rank > (zoomSettings?.allowRanking || 30)
                                  ? "error"
                                  : item.rank === 0
                                  ? "empty"
                                  : "success"
                              }`}
                            >
                              {item.rank === 999
                                ? "999+위"
                                : item.rank === 0
                                ? "-위"
                                : `${item.rank}위`}
                            </div>
                            <button
                              className={`remove-keyword ${
                                item.rank > (zoomSettings?.allowRanking || 0)
                                  ? "error"
                                  : ""
                              }`}
                              onClick={() => removeKeywordRank(index)}
                            >
                              ×
                            </button>
                          </div>
                          <span className="keyword-label">
                            *유입용 키워드{index + 1}(필수)
                          </span>
                          <input
                            type="text"
                            value={item.keyword}
                            onChange={(e) =>
                              handleKeywordChange(e, actualIndex)
                            }
                            onBlur={(e) => {
                              handleKeywordBlurWithValidation(
                                e.target.value,
                                index,
                                actualIndex
                              );
                            }}
                            onKeyDown={(e) =>
                              handleKeywordKeyDown(
                                e,
                                e.target.value,
                                index,
                                actualIndex
                              )
                            }
                            className={`keyword-input-field ${
                              keywordLoadingIndex === index ? "loading" : ""
                            }`}
                            disabled={
                              item.rank !== 0 || keywordLoadingIndex === index
                            } // 순위가 0이 아닌 경우 또는 로딩 중일 때 수정 불가
                          />
                          {keywordLoadingIndex === index && (
                            <div className="keyword-loading">
                              순위 조회 중...
                            </div>
                          )}
                        </>
                      ) : (
                        // 두 번째부터 (optional) - X 버튼 있음
                        <>
                          <div className="keyword-header">
                            <div
                              className={`keyword-rank ${
                                item.rank > (zoomSettings?.allowRanking || 30)
                                  ? "error"
                                  : item.rank === 0
                                  ? "empty"
                                  : "success"
                              }`}
                            >
                              {item.rank === 999
                                ? "999+위"
                                : item.rank === 0
                                ? "-위"
                                : `${item.rank}위`}
                            </div>
                            <button
                              className={`remove-keyword ${
                                item.rank > (zoomSettings?.allowRanking || 0)
                                  ? "error"
                                  : ""
                              }`}
                              onClick={() => removeKeywordRank(actualIndex)}
                            >
                              ×
                            </button>
                          </div>
                          <span className="keyword-label">
                            유입용 키워드{index + 1}(선택)
                          </span>
                          <input
                            type="text"
                            value={item.keyword}
                            onChange={(e) =>
                              handleKeywordChange(e, actualIndex)
                            }
                            onBlur={(e) => {
                              handleKeywordBlurWithValidation(
                                e.target.value,
                                index,
                                actualIndex
                              );
                            }}
                            onKeyDown={(e) =>
                              handleKeywordKeyDown(
                                e,
                                e.target.value,
                                index,
                                actualIndex
                              )
                            }
                            className={`keyword-input-field ${
                              keywordLoadingIndex === index ? "loading" : ""
                            }`}
                            disabled={
                              item.rank !== 0 || keywordLoadingIndex === index
                            } // 순위가 0이 아닌 경우 또는 로딩 중일 때 수정 불가
                          />
                          {keywordLoadingIndex === index && (
                            <div className="keyword-loading">
                              순위 조회 중...
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

            {keywordRanks.filter((item) => item.selected).length < 10 && (
              <button className="add-keyword-btn" onClick={addKeywordRank}>
                +
              </button>
            )}
          </div>
        </div>

        {/* 3. 가격 및 수량 정보 섹션 */}
        <div className="pricing-section">
          <div className="pricing-grid">
            <div className="form-field">
              <label>1회 단가</label>
              <input
                type="text"
                value={`${parseInt(formData.userPriceByOne) || 0}원`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>신청 수량</label>
              <input
                type="text"
                value={`${formData.totalQuantity || 0}회`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>결제 금액</label>
              <input
                type="text"
                value={`${(parseInt(formData.price) || 0).toLocaleString()}원`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>광고 마감일(예정일)</label>
              <input
                type="text"
                value={
                  formData.endDate
                    ? formData.endDate.slice(2, 10).replace(/-/g, "-")
                    : ""
                }
                readOnly
              />
            </div>
          </div>
        </div>

        {/* 키워드 선택 모달 */}
        <KeywordSelectionModal
          isOpen={isKeywordModalOpen}
          onClose={handleCloseKeywordModal}
          keywordRanks={keywordRanks}
          onKeywordRanksChange={handleKeywordRanksChange}
          zoomSettings={zoomSettings}
        />
      </div>
    );
  }

  // 완료 화면
  if (currentStep === 3) {
    return (
      <div className="advertisement-complete">
        <div className="complete-content">
          <div className="complete-icon">✅</div>
          <h2 className="complete-title">광고 등록이 완료되었습니다</h2>
          <p className="complete-description">
            등록하신 광고는 관리자 검토 후 승인됩니다.
            <br />
            승인 상태는 내 광고 확인에서 확인하실 수 있습니다.
          </p>
          <div className="complete-actions">
            <button
              className="complete-btn primary"
              onClick={() => onNext("myAdvertisement")}
            >
              내 광고 확인
            </button>
            <button
              className="complete-btn secondary"
              onClick={() => setCurrentStep(1)}
            >
              새 광고 등록
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shopping-visit-page-with-origin">
      {/* <div className="page-description">
        <h3>네이버 쇼핑 방문 광고 (원산지 포함)</h3>
        <p>
          상품 페이지를 방문하여 조회수를 높이는 광고 서비스입니다. 원산지
          정보가 포함됩니다.
        </p>
      </div> */}

      <div className="add-form-fields">
        {/* 첫 번째 줄: 상품 유형 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>상품 유형</label>
            <select
              value={formData.productName || ""}
              onChange={(e) => onInputChange("productName", e.target.value)}
              disabled={!user?.id}
              className="product-name-select"
            >
              <option value="태그">태그</option>
              <option value="정답">정답</option>
            </select>
          </div>
        </div>

        {/* 두 번째 줄: 상품 URL 주소와 메인키워드 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>상품 URL 주소</label>
            <input
              type="text"
              placeholder="https://smartstore.naver.com/..."
              value={formData.productUrl}
              onChange={handleUrlChange}
              onBlur={handleUrlBlur}
              disabled={!user?.id}
              className={urlError ? "error" : ""}
            />
            {urlError && <p className="error-message">{urlError}</p>}
          </div>
          <div className="add-form-field">
            <label>메인키워드(순위체크용)</label>
            <input
              type="text"
              placeholder="필수입력"
              value={formData.mainKeyword?.name || ""}
              onChange={(e) => onInputChange("mainKeyword", e.target.value)}
              disabled={!user?.id}
            />
          </div>
        </div>

        {/* 세 번째 줄: 카테고리 상품명과 카테고리 상품 URL */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>카테고리 상품명</label>
            <input
              type="text"
              placeholder="Enter"
              value={formData.missionName}
              onChange={(e) => onInputChange("missionName", e.target.value)}
              disabled={!user?.id}
            />
          </div>
          <div className="add-form-field">
            <label>
              카테고리 상품 URL
              <span className="add-info-icon">ⓘ</span>
              <span className="add-info-text">
                카테고리 상품일 시에만 입력해 주세요
              </span>
            </label>
            <input
              type="text"
              placeholder="선택입력"
              value={formData.originId}
              onChange={handleOriginIdChange}
              onBlur={handleOriginIdBlur}
              disabled={!user?.id}
              className={originIdError ? "error" : ""}
            />
            {originIdError && <p className="error-message">{originIdError}</p>}
          </div>
        </div>

        {/* 네 번째 줄: 일일 수량, 광고 시작일, 광고 종료일 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>일일 수량</label>
            <input
              type="text"
              placeholder="100회 이상 입력"
              value={formData.dailyQuantity}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                onInputChange("dailyQuantity", value);
              }}
              disabled={!user?.id}
            />
          </div>
          <div className="add-form-field">
            <label>광고 시작일</label>
            <input
              type="date"
              value={formData.adStartDate}
              onChange={handleAdStartDateChange}
              disabled={!user?.id}
              min={effectiveMinDate}
              onKeyDown={(e) => e.preventDefault()} // 키보드 입력 방지
              onPaste={(e) => e.preventDefault()} // 붙여넣기 방지
            />
          </div>
          <div className="add-form-field">
            <label>광고 종료일</label>
            <input
              type="date"
              value={formData.adEndDate}
              onChange={handleAdEndDateChange}
              disabled={!user?.id || !formData.adStartDate}
              min={getEndDateMin()}
              onKeyDown={(e) => e.preventDefault()} // 키보드 입력 방지
              onPaste={(e) => e.preventDefault()} // 붙여넣기 방지
            />
          </div>
        </div>

        {/* 다섯 번째 줄: 결제 금액 정보 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>1회 단가</label>
            <input
              type="text"
              value={`${parseInt(formData.userPriceByOne) || 0}원`}
              readOnly
            />
          </div>
          <div className="add-form-field">
            <label>신청 수량</label>
            <input
              type="text"
              value={`${formData.totalQuantity || 0}회`}
              readOnly
            />
          </div>
          <div className="add-form-field">
            <label>결제 금액</label>
            <input
              type="text"
              value={`${(parseInt(formData.price) || 0).toLocaleString()}원`}
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingVisitPageWithOrigin;
